<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="pdp-story-container pt-0">
    <div class="row mb-3">
      <!-- Title section -->
      <section
        id="pdp-head-section"
        class="col-lg-7 col-xl-8"
      >
        <!-- Preview message -->
        <div
          v-if="deal.is_preview"
          class="pdp-preview-box"
        >
          <div class="alert alert-warning my-3">
            <font-awesome-icon
              icon="exclamation-triangle"
              class="mr-3 alert-icon"
            />
            <span class="alert-text">
              This is a preview of your product detail page.
            </span>
          </div>
        </div>

        <!-- Experiment above the fold: enabled - hide on mobile -->
        <div class="d-none d-lg-block">
          <div
            v-if="hasListingTypeLogo || showWishlists"
            class="d-flex justify-content-space-between icon-container-spacing"
          >
            <ListingTypeLogo
              v-if="hasListingTypeLogo"
              class="listing-type-logo-container"
              :public-name="deal.public_name"
              :listing-type="deal.listing_type"
              tooltip-id="mobile-listing-type-tooltip"
            />
            <div
              class="pdp-interaction-container d-lg-none"
            >
              <ShareModal
                class="mr-2 mr-4-sm"
                :shareable-text="shareableLink"
                :title="deal.public_name"
                :subheader="story?.subheader"
                label-text="Or copy link"
                button-class="mobile-share-icon-container"
                icon-class="dark-mode"
                wrapper-class="modal-wishlists"
                :params="socialParams"
              />
              <WishlistsButton
                v-if="showWishlists"
                :deal-id="deal.id"
                :deals-count="wishlistsCount"
              />
            </div>
          </div>

          <TacoRating
            v-if="!deal.is_preview && !deal.is_info"
            class="d-lg-none mb-3"
            :deal="deal"
          />
        </div>

        <div
          class="d-lg-none"
        >
          <div class="pdp-above-fold-section">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h1 class="pdp-section-title h2">
                {{ deal.public_name }}
              </h1>
              <p v-if="!deal.is_info" class="pdp-plan-types h5">
                {{ planTypeText }}
              </p>
            </div>
            <h2
              v-if="story.subheader"
              class="pdp-text mt-1 mb-0"
            >
              {{ story.subheader }}
            </h2>
            <div class="mt-1">
              <div
                v-if="deal.is_free"
                class="d-flex align-items-center"
              >
                <font-awesome-icon
                  :icon="['far', 'arrow-alt-circle-down']"
                  class="icon mr-1"
                />
                <strong>
                  Total Downloads: {{ deal.freebie_downloads_total.toLocaleString('en-US') }}
                </strong>
              </div>
              <div
                v-else-if="deal.deal_review && deal.deal_review.review_count"
                class="align-items-center d-flex"
              >
                <TacoRating
                  v-if="!deal.is_preview && !deal.is_info"
                  :deal="deal"
                />
              </div>
              <div v-else>
                <span>
                  No reviews yet,
                  <a href="#reviews">be the first!</a>
                </span>
              </div>
            </div>

            <div class="mt-3 pdp-details-wrapper row">
              <section
                id="pdp-carousel-section"
                class="pdp-main-section col mb-1"
              >
                <PDPCarousel
                  v-if="images.length"
                  class="d-block d-lg-none"
                  :deal-id="deal.id"
                  :deal-name="deal.public_name"
                  :images="images"
                  :show-share-modal="true"
                  :show-wishlists="showWishlists"
                  :social-params="socialParams"
                  :shareable-link="shareableLink"
                />

                <ListingTypeLogo
                  v-if="hasListingTypeLogo"
                  class="justify-content-center mt-3"
                  :public-name="deal.public_name"
                  :listing-type="deal.listing_type"
                  tooltip-id="mobile-listing-type-tooltip"
                />
              </section>
            </div>
          </div>

          <PDPPricingInfo
            :deal="deal"
            :buy-button="buyButton"
            :plans="deal.plans"
            :user-has-plus="user.hasPlus"
            :is-pricing-card-email-submitted="isPricingCardEmailSubmitted"
            display-plan-type
            class="mt-3"
            @changePlan="onChangePlan"
          />
          <!-- Upsell Callout contianer -->
          <DealUpsellCallout
            v-if="deal.upsell_callout"
            class="mt-3 mb-0"
            :upsell-callout="deal.upsell_callout"
          />

          <!-- Mobile sticky info -->
          <PDPPricingSticky
            :deal="deal"
            :user="user"
            :buy-button="buyButton"
            :is-pricing-card-email-submitted="isPricingCardEmailSubmitted"
            :banner-details="deal.banner_details"
          />
        </div>
      </section>
    </div>

    <div class="pdp-details-wrapper row">
      <section
        id="pdp-carousel-section"
        class="pdp-main-section col-lg-7 col-xl-8 mb-3"
      >
        <Breadcrumbs
          v-if="showBreadcrumbs"
          class="d-none d-lg-block mb-3"
          :deal-name="deal.public_name"
          :group="breadcrumbObjects.find((c) => c.slug === 'group')"
          :category="breadcrumbObjects.find((c) => c.slug === 'category')"
          :subcategory="breadcrumbObjects.find((c) => c.slug === 'subcategory')"
        />

        <ProductsMainImage
          v-if="images.length || story.videos.length"
          :images="images"
          :videos="story.videos"
          class="d-none d-lg-block"
        />

        <!-- Experiment above the fold: enabled -->
        <div
          class="mb-3 d-lg-none"
        >
          <PricingCardGuaranteeWrapper
            v-if="deal.money_back_callout_type"
            class="mt-0"
            :callout-type="deal.money_back_callout_type"
            :refundable-days="deal.refundable_days"
            :is-freebie="deal.is_free"
            :interval="planInterval"
          />

          <div
            v-if="isReportable"
            class="text-center mt-3"
          >
            <a
              :href="reportUrl"
              target="_blank"
            >
              Report listing
              <font-awesome-icon
                icon="external-link-alt"
                height="16"
                width="16"
              />
            </a>
          </div>
        </div>
        <div
          v-if="story.snippet"
          class="pdp-description mt-3"
          v-html="story.snippet"
        />
        <PDPTldr
          v-if="showTldr"
          class="mt-5"
          :highlights="story.highlights"
        />
        <ProductsAtGlance
          v-if="attributes.length"
          :attributes="attributes"
          class="mt-5"
        />

        <ProductsOverview
          v-if="deal.overview_media.length"
          class="mt-5"
          :deal="deal"
        />
        <!-- jw: this does not include the pricing table. That is rendered just below outside of the
                 two columns that this is rendered within.
        -->
        <DealTermsAndFeatures
          v-if="isActiveDeal"
          class="mt-5"
          :deal="deal"
        />
      </section>

      <section class="d-none d-lg-block col-lg-5 col-xl-4">
        <div class="interaction-active pdp-purchase-section-whitespace">
          <div
            class="pdp-interaction-container"
          >
            <ShareModal
              class="mr-3"
              :shareable-text="shareableLink"
              :title="deal.public_name"
              :subheader="story?.subheader"
              wrapper-class="modal-wishlists"
              icon-class="dark-mode mr-1"
              label-text="Or copy link"
              :params="socialParams"
            />
            <WishlistsButton
              v-if="showWishlists"
              :deal-id="deal.id"
              :deals-count="wishlistsCount"
            />
          </div>
          <PDPPricingCard
            :deal="deal"
            :buy-button="buyButton"
            :plan-type-text="sentenceCase(planTypeText)"
            :subheader="story.subheader"
            :plans="deal.plans"
            :user="user"
            id-suffix="02"
            :is-pricing-card-email-submitted="isPricingCardEmailSubmitted"
            :banner-details="deal.banner_details"
            :show-banner-details="showBannerDetails"
            :display-timer="displayTimer"
            :plan-interval="planInterval"
            @email-submitted="onEmailSubmitted"
          />
        </div>
      </section>
    </div>
    <!-- jw: this is not intuitive, but this is the second half of the DealTermsAndFeatures above
              but it needs to be rendered full width for desktop which is why it is rendered outside
              of the two column UI from above.
    -->
    <PricingTable
      v-if="isActiveDeal"
      :deal="deal"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import {
  ATTRIBUTE_TYPE,
  BLACKLIST_ATTRIBUTE,
} from '@/constants/global';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ProductsMainImage from './carousel/ProductsMainImage.vue';
import PDPPricingCard from './PDPPricingCard.vue';
import PDPPricingInfo from './PDPPricingInfo.vue';
import PDPPricingSticky from './PDPPricingSticky.vue';
import ShareModal from '../common/ShareModal.vue';
import { LISTING_TYPES_WITH_LOGO } from '~/constants/listing-types';
import { getUtmParamsForSocialShare, getPlanInterval } from '../../util/global';
import ProductsOverview from '../products/overview/ProductsOverview.vue';
import TacoRating from '../utilities/TacoRating.vue';
import Breadcrumbs from '~/components/pdp/Breadcrumbs';

export default {
  LISTING_TYPES_WITH_LOGO,
  components: {
    ProductsMainImage,
    PDPPricingCard,
    PDPPricingInfo,
    PDPPricingSticky,
    ShareModal,
    ProductsOverview,
    TacoRating,
    Breadcrumbs,
    FontAwesomeIcon,
    // dynamic import based on condition (separate chunk per file)
    DealUpsellCallout: () => import('./util/DealUpsellCallout.vue'),
    PricingCardGuaranteeWrapper: () => import('./PricingCardGuaranteeWrapper.vue'),
    PDPTldr: () => import('./PDPTldr.vue'),
    DealTermsAndFeatures: () => import('../products/terms-and-features/DealTermsAndFeatures.vue'),
    ListingTypeLogo: () => import('./ListingTypeLogo.vue'),
    WishlistsButton: () => import('../wishlists/Button.vue'),
    ProductsAtGlance: () => import('../products/at-a-glance/ProductsAtGlance.vue'),
    PricingTable: () => import('../products/pricing-table/PricingTable.vue'),
    PDPCarousel: () => import('./carousel/PDPCarousel.vue'),
  },
  props: {
    deal: {
      type: Object,
      required: true,
    },
    // TODO temp user object to be replaced by this.$store.state.session.user
    user: {
      type: Object,
      required: true,
    },
    showBannerDetails: {
      type: Boolean,
      default: false,
    },
    displayTimer: {
      type: Boolean,
      default: false,
    },
    showBreadcrumbs: {
      type: Boolean,
      default: false,
    },
    breadcrumbObjects: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isPricingCardEmailSubmitted: false,
      tldrExperimentVariant: '',
    };
  },
  async fetch() {
    if (this.showWishlists) {
      await this.$store.dispatch('wishlists/fetchWishlistsStatByDeal', this.deal.id);
    }

    this.tldrExperimentVariant = await this.$experiments.enroll('pdp-show-tldr');
  },
  computed: {
    selectedPlan() {
      return this.$store.state.selectedPlan?.plan;
    },
    hasListingTypeLogo() {
      return !!this.$options.LISTING_TYPES_WITH_LOGO.includes(this.deal.listing_type);
    },
    story() {
      return this.deal?.products?.[0]?.story || {};
    },
    buyButton() {
      return this.deal.buy_button || {};
    },
    images() {
      if (this.deal.media_url) {
        return [
          {
            alt_text: 'Featured image',
            content: '',
            description: '',
            image: this.deal.media_url,
          },
          ...this.story?.images || [],
        ];
      }
      return this.story?.images || [];
    },
    planTypeText() {
      let textStr = '';
      for (let i = 0; i < this.deal.unique_plan_types.length; i += 1) {
        const typeArr = this.deal.unique_plan_types[i];
        if (typeArr[0] === 'One Time Purchase') {
          textStr += 'Lifetime deal';
        } else if (typeArr[0] === 'Limited License') {
          textStr += `${typeArr[2]} ${typeArr[1]} DEAL`;
        } else {
          textStr += typeArr[0];
        }
        if (i < this.deal.unique_plan_types.length - 1) {
          textStr += ' & ';
        }
      }
      return textStr;
    },
    isActiveDeal() {
      return !this.deal.stage_details.is_inactive_indefinitely;
    },
    showTldr() {
      if (this.tldrExperimentVariant !== 'show') return false;

      return !!this.story?.highlights?.length && !this.deal.is_info;
    },
    currentUser() {
      return this.$store.state.session.user;
    },
    wishlistsCount() {
      return this.$store.getters['wishlists/wishlistsCount'];
    },
    wishlistExperiment() {
      return this.$store.getters['wishlists/wishlistExperiment'];
    },
    showWishlists() {
      return !!(this.currentUser && this.wishlistExperiment === 'enabled' && !this.deal.is_info);
    },
    attributes() {
      return this.deal
        .attributes?.filter((attribute) => {
          if (BLACKLIST_ATTRIBUTE.includes(attribute.slug)) {
            return false;
          }

          // for boolean types filter only ones with values: true
          return !(attribute.type === ATTRIBUTE_TYPE.BOOLEAN && !attribute.values);
        });
    },
    socialParams() {
      return getUtmParamsForSocialShare(this.deal, this.currentUser?.id);
    },
    shareableLink() {
      if (process.client) {
        return `${window.location.origin}${this.deal.get_absolute_url}`;
      }

      return '';
    },
    isReportable() {
      // info deals are 'marketplace', but are controlled internally
      if (!this.deal.is_marketplace || this.deal.is_info) {
        return false;
      }

      return this.user?.isAuthenticated || this.user?.isStaff;
    },
    reportUrl() {
      return `https://report-offer.paperform.co/?deal_id=${this.deal.id}&deal_slug=
              ${this.deal.slug}&email=${this.user.email}`;
    },
    currentlyBeforeNextDealStage() {
      const nextStageStartDate = this.deal.stage_details?.next_stage_start_date;

      if (!nextStageStartDate) {
        return false;
      }

      const dayjsNow = dayjs();
      const diffInMilliseconds = dayjs(nextStageStartDate).diff(dayjsNow);

      return diffInMilliseconds > 0;
    },
    planInterval() {
      return getPlanInterval(this.deal?.unique_plan_types);
    },
  },
  methods: {
    sentenceCase(str) {
      return str.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, (c) => c.toUpperCase());
    },
    onEmailSubmitted() {
      this.isPricingCardEmailSubmitted = true;
    },
    onChangePlan(plan) {
      this.$store.dispatch(
        'selectedPlan/updatePlan',
        { selectedPlan: plan },
      );
    },
  },
};
</script>

<style>
  .gap-2 {
    gap: 0.5rem;
  }

  /*  react to navbar scroll behavior (.navbar-visible is attached to body) */
  .navbar-visible .pdp-purchase-section-whitespace {
    top: calc(173px + var(--banner-height, 0px)) !important;
  }

  /* Prevent overflow issues (.overflow-over-navigation is attached to body) */
  /* stylelint-disable-next-line selector-class-pattern */
  .with_fixed_header.navbar-visible .pdp-purchase-section {
    top: calc(216px + var(--banner-height, 0px)) !important;
  }
</style>

<style scoped lang="scss">
  @import '~/styles/dorado/themes/dorado';
  @import '~/styles/utils/breakpoints';

  // Set custom styles for share modal
  div ::v-deep {
    .mobile-share-icon-container {
      background-color: $gray-100;
      border-radius: 50%;
      height: 40px;
      padding: 9px 12px;
      width: 40px;
    }

    .dark-mode path {
      fill: $forest;
    }
  }

  /* stylelint-disable selector-no-qualifying-type */
  h2.pdp-text {
    font-weight: 400 !important;
  }

  .pdp-story-container {
    --taco-height: 16px;
    --taco-width: 105px;
  }

  .pdp-description ::v-deep b {
    font-weight: 700;
  }

  .pdp-interaction-container {
    display: flex;
    flex: 1;
    height: auto;
    justify-content: end;
    margin-bottom: 1rem;
  }

  .listing-type-logo-container {
    flex: 0;
  }

  @include media-breakpoint-up(lg) {
    .pdp-story-container {
      --taco-height: 24px;
      --taco-width: 114px;

      padding-top: 32px;
    }

    .pt-0 {
      padding-top: 0;
    }

    .listing-type-logo-container {
      display: none !important;
    }
  }

  .pdp-product-subtitle {
    font-family: Roboto, Verdana, sans-serif;
    font-weight: 400 !important;
  }

  .pdp-purchase-section-whitespace {
    position: sticky;
    // Setting top dynamically if top banner is present otherwise use 0px by default.
    // See static/js/banner.js
    top: calc(86px + var(--banner-height, 0px));
    transition: top 0.4s ease-in-out;
    z-index: 1020;
    margin-bottom: 16px;

    &.interaction-active {
      margin-top: 0;
    }
  }

  .pdp-page-title {
    margin: 0;
  }

  // PDP Above the Fold experiment
  .pdp-section-title {
    font-size: 24px !important;
    line-height: 30px !important;
    margin-bottom: 0;
  }

  p.pdp-plan-types {
    margin-top: 10px;
    font-weight: 600;
    font-size: 14px;
    line-height: 21px;
    color: $grace;
    text-transform: lowercase;
  }

  p.pdp-plan-types::first-letter {
    text-transform: capitalize;
  }

  // TODO:BRO-1276: Remove this custom CSS
  // Dorado alert styles - Overrides legacy ones
  .alert {
    align-items: flex-start;
    border: 1px solid transparent;
    border-radius: 0;
    display: flex;
    padding: 1rem;
    position: relative;

    &.alert-warning {
      color: $dark;
      background-color: $warning;
      border-color: $warning;
      font-size: 0.875rem;
    }

    .alert-icon {
      font-size: 24px;
      margin: auto 0;
    }
  }

  @include media-breakpoint-down(md) {
    .pdp-interaction-container {
      height: 1.25rem;
    }

    .icon-container-spacing {
      padding-bottom: 1rem;
    }
  }
  /* stylelint-enable selector-no-qualifying-type */
</style>
